import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, Box, Alert } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { resetPassword, validateResetToken } from "../../client/api";
import { useGlobalStyles } from "../../theme/global";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const classes = useGlobalStyles();

  const token = searchParams.get("token");

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }

      try {
        const response = await validateResetToken(token);
        setTokenValid(response.valid);
      } catch (err: any) {
        setTokenValid(false);
        setError("Invalid or expired reset token");
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (!token) {
      setError("Invalid reset token");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await resetPassword(token, password);
      setMessage("Password reset successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "An error occurred while resetting password"
      );
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <Box
        sx={{ maxWidth: 400, margin: "auto", padding: 2, textAlign: "center" }}
      >
        <Typography>Validating token...</Typography>
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box sx={{ maxWidth: 400, margin: "auto", padding: 2 }}>
        <Alert severity="error">
          Invalid or expired reset token. Please request a new password reset.
        </Alert>
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={() => navigate("/request-password-reset")}
          >
            Request New Reset
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, margin: "auto", padding: 2 }}>
      <Typography variant="h5">Set New Password</Typography>
      <br />

      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="New Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          sx={{ mb: 2 }}
          helperText="Password must be at least 6 characters"
          disabled={loading}
          className={classes.textFieldLabel}
        />
        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          sx={{ mb: 2 }}
          disabled={loading}
          className={classes.textFieldLabel}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          fullWidth
        >
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    </Box>
  );
};

export default ResetPassword;
